import * as React from 'react';
import { captureException } from '@sentry/react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** When set, render this instead of the default error UI when an error is caught. Use null to silently swallow. */
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  componentStack?: string;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Store component stack for display and persist for debug UI
    this.setState((s) => ({ ...s, componentStack: errorInfo.componentStack ?? undefined }));
    if (typeof window !== 'undefined') {
      (window as any).__lastErrorBoundaryComponentStack = errorInfo.componentStack ?? '';
      console.error('[ErrorBoundary] Caught error:', error.message);
      console.error('[ErrorBoundary] Error stack:', error.stack);
      console.error('[ErrorBoundary] React component stack (shows which component threw):', errorInfo.componentStack);
    }
    // Only capture in browser (Sentry may not be initialized during SSR)
    if (typeof window !== 'undefined' && typeof captureException === 'function') {
      captureException(error, {
        tags: {
          component: 'ErrorBoundary',
          errorType: 'react-error-boundary',
        },
        extra: {
          componentStack: errorInfo.componentStack,
        },
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
      });
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }
      return (
        <div style={{ backgroundColor: 'orange', padding: '20px', color: 'white', maxWidth: '100%', overflow: 'auto' }}>
          <h2>Something went wrong while rendering this section:</h2>
          <pre>{this.state.error?.toString()}</pre>
          <p style={{ marginTop: 12, fontWeight: 'bold' }}>Component stack (where it threw):</p>
          <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {this.state.componentStack ?? (typeof window !== 'undefined' && (window as any).__lastErrorBoundaryComponentStack) ?? 'Check console for [ErrorBoundary] React component stack'}
          </pre>
        </div>
      );
    }

    return this.props.children;
  }
}
